import { createSelector } from '@ngrx/store';
import { OrderModelType } from '../../../constants';
import { AllOmsOrder, AllOmsOrserEntities } from '../reducers/oms.reducer';
import { IBaseOrderEntity, OMSOrder, OfflineOrder, Side } from '@easy-lib/modules/order';
import { AppState } from '@easy-lib/state/app.state';

const getOmsState = (state: AppState) => state.omsState;
export const omsOrders = createSelector(getOmsState, state => Object.values(state.entities));
export const algoSuffixCharacters = createSelector(getOmsState, state => state.algoSuffixCharacters);
export const olgoOmsOrders = createSelector(omsOrders, orders => {
	return orders.filter(t => !!t.referenceId);
});
const getDraftState = (state: AppState) => state.drftState;
export const draftOrders = createSelector(getDraftState, state => Object.values(state.entities)); //draftAdapter.getSelectors().selectAll;

const getOfflineState = (state: AppState) => state.offlineState;

export const omsOrdersExceptAlgo = createSelector(omsOrders, orders => orders.filter(res => !res.referenceId));
export const offlineOrders = createSelector(getOfflineState, state => Object.values(state.entities)); //draftAdapter.getSelectors().selectAll;
export const tradeLastUpdateTime = createSelector(omsOrders, (orders: OMSOrder[]) => {
	const time = orders
		?.map(t => t.trades)
		?.flat()
		?.filter(Boolean)
		?.map(t => new Date(t.dateTime).getTime());
	if (time.length) return Math.max(...time);
	return Infinity;
});
export const getAllOrder = createSelector(
	omsOrdersExceptAlgo,
	draftOrders,
	offlineOrders,
	(omsOrders, draftOrders, offlineOrders) => {
		const orders = [...omsOrders, ...draftOrders, ...offlineOrders];
		return orders.sort(
			(a, b) => new Date(b.createDateTime).getTime() - new Date(a.createDateTime).getTime()
		) as IBaseOrderEntity[];
	}
);
export const getOpenOrders = createSelector(omsOrders, orders =>
	orders
		.filter((t: OMSOrder) => t.isOpenOrder)
		.map(t => ({ symbolIsin: t.symbolIsin, side: t.side, quantity: t.RemainingQuantity as number }))
);
export const getOpenBuyOrders = createSelector(getOpenOrders, orders =>
	orders.filter(t => t.side === Side.Buy).groupBy('symbolIsin')
);
export const getOpenSellOrders = createSelector(getOpenOrders, orders =>
	orders.filter(t => t.side === Side.Sell).groupBy('symbolIsin')
);
export const getLoading = createSelector(getOmsState, state => state.loading);

export const selectStore = (orderModelType?: OrderModelType) =>
	createSelector(getAllOrder, orders =>
		orders.filter(t => t.orderModelType === (orderModelType ?? t.orderModelType))
	);

export const getOrder = (id: string) => createSelector(getAllOrder, orders => orders.find(order => order.id === id));
export const getOrderBySymbolIsin = (symbolIsin: string) =>
	createSelector(getAllOrder, orders => orders.find(order => order.symbolIsin === symbolIsin));

export const getOmsOrder = (id: string) =>
	createSelector(omsOrders, orders => {
		const order = orders.find(order => order.id === id);
		return order ? new OMSOrder({ ...order }) : undefined;
	});

export const getOmsOrderByHon = (hon: number) =>
	createSelector(omsOrders, orders => {
		const order = orders.find((order: OMSOrder) => order.hon === hon);
		return order ? new OMSOrder({ ...order }) : undefined;
	});
export const getOfflineOrder = (id: string) =>
	createSelector(offlineOrders, orders => {
		const order = orders.find(order => order.id === id);
		return order ? new OfflineOrder({ ...order }) : undefined;
	});

export const getOrders = (ids?: string[], symbolIsin?: string, type?: OrderModelType) =>
	createSelector(getAllOrder, orders => {
		let values = [...orders];
		if (ids) values = values.filter(res => ids.map(t => t.trim()).includes(res.id.trim()));
		if (symbolIsin) values = values.filter(res => res.symbolIsin === symbolIsin);
		if (type) values = values.filter(res => res.orderModelType === type);
		return values;
	});

export const getOrderIdsForDelete = (ids?: string[], orderModelType?: OrderModelType) =>
	createSelector(getAllOrder, orders => {
		let filteredOrders = orders.filter(t => (ids?.length ? ids?.includes(t.id) : t.id));
		filteredOrders = filteredOrders.filter(
			order => order.orderModelType === (orderModelType ?? order.orderModelType)
		);
		// filteredOrders = filteredOrders.filter(t => !t.IsInErrorState);
		return filteredOrders.map(t => t.id);
	});

export const getOpenOrder = (symbolIsin: string, side: Side) =>
	createSelector(omsOrders, (orders: OMSOrder[]) =>
		orders
			.filter(t => t.symbolIsin === symbolIsin && t.side === side && t.isOpenOrder)
			.map(t => t.RemainingQuantity)
			.reduce((a, b) => a + b, 0)
	);

export const getordersTest = () => createSelector(AllOmsOrder, state => ({ omsState: state }));
export const getordersTest2 = () => createSelector(AllOmsOrserEntities, state => ({ omsState2: state }));
